/**
 * A sales person's decision on one draft line: Confirm, Pick item, No item, Undo.
 *
 * `LineActions` are the buttons, `DecisionNote` says who decided what, and
 * `PickItemDialog` offers I2Q's candidates first and a search in the item catalogue of
 * the case's area below. A matched line has a plain "Confirm" next to "Pick item". An
 * item whose name shares no word with the line carries a quiet warning (D7, 19 Oct 2026).
 *
 * A decision is stored in the app database only. It never changes what I2Q proposed,
 * so the agreement figure on the dashboard keeps measuring I2Q.
 */

import { Check, CircleSlash, Icon, PackageSearch, Search, Undo2 } from "@norican/icons";
import { Alert, Badge, Button, Dialog, Input, Spinner, Typography } from "@norican/react";
import { useState } from "react";

import { FeedbackControl } from "@/features/case-review/feedback-control";
import { itemNameDiffers } from "@/features/case-review/item-name-match";
import { effectiveItem } from "@/features/case-review/line-semantics";
import {
  type ApiError,
  LINE_TOPICS,
  type LineDecision,
  type LineStatus,
  personName,
  SEARCH_MIN_CHARS,
  useCatalogueSearch,
  useLineDecision,
  workFailureText,
} from "@/lib/api";
import { FEATURES } from "@/lib/features";
import { logger } from "@/lib/logger";
import { type QuoteLine } from "@/lib/mock";
import { useDialogUsage } from "@/lib/usage-react";
import {
  decisionSentence,
  I2Q_EXACT_NOTE,
  pickNameDiffersText,
  twinDecisionRequest,
  twinDecisionSentence,
} from "@/lib/work-labels";

// Shared with the Outlook pane (review F05, 11 Oct 2026): one meaning on every surface.
export { effectiveItem };

/** Who decided, and whether the engine proposes something else by now. */
export function DecisionNote({ line }: { line: QuoteLine }) {
  const decision: LineDecision | null | undefined = line.decision;
  if (!decision) {
    return null;
  }
  return (
    <span className="flex flex-col text-xs">
      <span className="text-fg-muted break-words">{decisionSentence(decision, personName)}</span>
      {decision.by_i2q && <span className="text-fg-subtle break-words">{I2Q_EXACT_NOTE}</span>}
      {decision.engine_changed && (
        <span className="text-warning-fg break-words">
          {line.matchedItemId
            ? `I2Q now proposes ${line.matchedItemId}`
            : "I2Q now proposes no item"}
        </span>
      )}
    </span>
  );
}

/** The tag of a decided line. */
export function DecisionBadge({ decision }: { decision: LineDecision }) {
  const meta = decision.by_i2q
    ? { tone: "info" as const, label: "Decided by I2Q" }
    : decision.kind === "confirm"
      ? { tone: "success" as const, label: "Confirmed" }
      : decision.kind === "item"
        ? { tone: "success" as const, label: "Picked" }
        : { tone: "neutral" as const, label: "No item" };
  return (
    <Badge tone={meta.tone} size="sm" className="h-auto w-fit text-left whitespace-normal">
      {meta.label}
    </Badge>
  );
}

export interface LineActionsProps {
  caseId: string;
  /** Position of the line in the stored draft (what the API expects). */
  lineIndex: number;
  line: QuoteLine;
  status: LineStatus;
  /** Opens the pick dialog for this line. */
  onPick: () => void;
  /** Buttons side by side instead of stacked. */
  inline?: boolean;
}

/** The decision buttons of one line. Nothing without a line key (a backend before stage 2). */
export function LineActions({ caseId, lineIndex, line, status, onPick, inline }: LineActionsProps) {
  const decide = useLineDecision(caseId);
  const lineKey = line.lineKey;
  if (!lineKey) {
    return null;
  }
  const error = workFailureText(decide.error, "line");
  const send = (kind: "confirm" | "no_item" | "clear") => {
    logger.debug("[case] line decision", { case: caseId, lineIndex, kind });
    decide.mutate({ lineIndex, line_key: lineKey, kind });
  };
  const label = line.enquiryText;
  // sim360 P18: the first mail of the same inquiry has a decision on this line
  const twin =
    line.decision || line.requestAction === "remove" ? null : (line.twinDecision ?? null);
  const useTwin = () => {
    if (!twin) {
      return;
    }
    const request = twinDecisionRequest(twin, line.matchedItemId);
    logger.debug("[case] line decision from the first mail", {
      case: caseId,
      lineIndex,
      kind: request.kind,
    });
    decide.mutate({ lineIndex, line_key: lineKey, ...request });
  };

  return (
    <div className="flex flex-col gap-1">
      {line.requestAction === "remove" && (
        <span className="text-warning-fg text-xs break-words">
          The customer removed this line. Check the revised inquiry. Then press No item.
        </span>
      )}
      {twin && (
        <span className="flex flex-wrap items-center gap-2 text-xs" data-twin-decision>
          <span className="text-fg-muted break-words">
            {twinDecisionSentence(twin, personName)}
          </span>
          <Button
            size="sm"
            variant="outline"
            tone="accent"
            loading={decide.isPending}
            startIcon={<Icon icon={Check} size="xs" />}
            aria-label={`Use the decision of ${twin.case_id} for ${label}`}
            onClick={useTwin}
          >
            Use this
          </Button>
        </span>
      )}
      <div
        className={inline ? "flex flex-wrap items-center gap-2" : "flex flex-col items-end gap-1"}
      >
        {line.requestAction === "remove" && line.decision && line.decision.kind !== "no_item" && (
          <Button
            size="sm"
            variant="outline"
            tone="neutral"
            disabled={decide.isPending}
            onClick={() => send("no_item")}
          >
            No item
          </Button>
        )}
        {line.decision ? (
          <Button
            size="sm"
            variant="ghost"
            tone="neutral"
            loading={decide.isPending}
            startIcon={<Icon icon={Undo2} size="xs" />}
            aria-label={
              line.decision.by_i2q
                ? `Undo I2Q's decision for ${label}`
                : `Undo the decision for ${label}`
            }
            onClick={() => send("clear")}
          >
            Undo
          </Button>
        ) : (
          <>
            {line.requestAction !== "remove" &&
              (status === "suggested" || (FEATURES.acceptMatched && status === "matched")) &&
              line.matchedItemId && (
                <Button
                  size="sm"
                  tone="accent"
                  loading={decide.isPending}
                  startIcon={<Icon icon={Check} size="xs" />}
                  aria-label={`Confirm ${line.matchedItemId} for ${label}`}
                  onClick={() => send("confirm")}
                >
                  Confirm
                </Button>
              )}
            {line.requestAction !== "remove" && (
              <Button
                size="sm"
                variant="outline"
                tone="neutral"
                disabled={decide.isPending}
                startIcon={<Icon icon={PackageSearch} size="xs" />}
                aria-label={`Pick an item for ${label}`}
                onClick={onPick}
              >
                Pick item
              </Button>
            )}
            <Button
              size="sm"
              variant="ghost"
              tone="neutral"
              disabled={decide.isPending}
              startIcon={<Icon icon={CircleSlash} size="xs" />}
              aria-label={`No item for ${label}`}
              onClick={() => send("no_item")}
            >
              No item
            </Button>
          </>
        )}
      </div>
      {error && (
        <span className="text-danger-fg text-xs break-words whitespace-normal" role="alert">
          {error}
        </span>
      )}
    </div>
  );
}

// ── pilot feedback ───────────────────────────────────────────────────────────

/**
 * Stage 4: "right / wrong" on one line, about its item, quantity or price. Nothing
 * without a line key (a backend before stage 2) or a position in the stored draft.
 */
export function LineFeedback({
  caseId,
  lineIndex,
  line,
}: {
  caseId: string;
  lineIndex: number;
  line: QuoteLine;
}) {
  if (!line.lineKey || lineIndex < 0) {
    return null;
  }
  return <FeedbackControl caseId={caseId} lineIndex={lineIndex} topics={LINE_TOPICS} />;
}

// ── pick dialog ──────────────────────────────────────────────────────────────

export interface PickOption {
  item: string;
  name: string | null;
  area: string | null;
  isSpare?: boolean | null;
}

/**
 * One item to pick. Decision D7 (19 Oct 2026): when its name shares no word with the line,
 * a quiet warning names the line under it. The "Pick" button stays as it is: the person
 * may know better (a successor, a set), so the pick still saves. An item I2Q itself
 * offers on the line (`suggested`) gets no warning: the engine found it by its number or
 * its catalogue name, and a line in another language shares no word with the catalogue.
 */
export function PickRow({
  option,
  lineText,
  suggested = false,
  busy,
  onPick,
}: {
  option: PickOption;
  /** What the customer asked on the line, compared with the item name. */
  lineText: string;
  /** The item is one of I2Q's candidates for the line. */
  suggested?: boolean;
  busy: boolean;
  onPick: (option: PickOption) => void;
}) {
  const differs = !suggested && itemNameDiffers(lineText, option.name);
  return (
    <li
      className="border-border-subtle flex items-center gap-3 border-b py-2 last:border-b-0"
      data-pick-name-differs={differs ? "" : undefined}
    >
      <span className="min-w-0 flex-1">
        <span className="text-fg block font-mono text-sm font-bold break-all">{option.item}</span>
        {option.name && (
          <span className="text-fg-muted block text-xs break-words">{option.name}</span>
        )}
        {differs && (
          <span className="text-warning-fg block text-xs break-words">
            {pickNameDiffersText(lineText)}
          </span>
        )}
        <span className="text-fg-subtle block text-xs">
          {[
            option.area ? `${option.area.toUpperCase()} catalogue` : null,
            option.isSpare === false ? "not flagged as spare part" : null,
            option.isSpare === true ? "spare part" : null,
          ]
            .filter(Boolean)
            .join(", ")}
        </span>
      </span>
      <Button
        size="sm"
        variant="outline"
        tone="accent"
        disabled={busy}
        aria-label={`Pick ${option.item}`}
        onClick={() => onPick(option)}
      >
        Pick
      </Button>
    </li>
  );
}

export interface PickItemDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  caseId: string;
  lineIndex: number;
  line: QuoteLine;
  /** Catalogue area of the case, used when a candidate names none. */
  caseArea: string | null;
  /** Runs after the pick was saved, with the item: the page says so in a toast with "Undo". */
  onPicked?: (item: string) => void;
}

/** Pick the item of one line: the engine's candidates first, the catalogue search below. */
export function PickItemDialog({
  open,
  onOpenChange,
  caseId,
  lineIndex,
  line,
  caseArea,
  onPicked,
}: PickItemDialogProps) {
  const decide = useLineDecision(caseId);
  const [text, setText] = useState("");
  const search = useCatalogueSearch(caseId, text);
  // The usage log: opened, saved, or closed without a save. Never the search text.
  const usage = useDialogUsage("pick_item", open, caseId);

  const candidates: PickOption[] = [];
  if (line.matchedItemId) {
    candidates.push({
      item: line.matchedItemId,
      name: line.itemName ?? null,
      area: line.catalogueArea ?? caseArea,
      isSpare: line.isSpare,
    });
  }
  for (const suggestion of line.suggestions ?? []) {
    if (
      !candidates.some(
        (c) => c.item === suggestion.item && c.area === (suggestion.area ?? caseArea),
      )
    ) {
      candidates.push({
        item: suggestion.item,
        name: suggestion.name ?? null,
        area: suggestion.area ?? caseArea,
      });
    }
  }

  const typed = text.trim();
  const searching =
    typed.length >= SEARCH_MIN_CHARS && (search.isFetching || search.term !== typed);
  const searchStatus = (search.error as ApiError | null)?.status;
  const results: PickOption[] = (search.data ?? []).map((hit) => ({
    item: hit.item,
    name: hit.name,
    area: hit.area,
    isSpare: hit.is_spare,
  }));
  const error = workFailureText(decide.error, "line");

  const pick = (option: PickOption) => {
    if (!line.lineKey) {
      return;
    }
    logger.debug("[case] pick item", {
      case: caseId,
      lineIndex,
      item: option.item,
      nameDiffers:
        !candidates.some((known) => known.item === option.item) &&
        itemNameDiffers(line.enquiryText, option.name),
    });
    decide.mutate(
      {
        lineIndex,
        line_key: line.lineKey,
        kind: "item",
        item: option.item,
        ...(option.area ? { item_area: option.area } : {}),
      },
      {
        onSuccess: () => {
          usage.saved();
          onPicked?.(option.item);
          onOpenChange(false);
        },
        onError: (err) => logger.warn("[case] pick", err),
      },
    );
  };

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Backdrop />
        <Dialog.Popup>
          <Dialog.Header>
            <Dialog.Title>
              <Typography as="span" variant="heading-6" className="text-fg">
                Pick the item
              </Typography>
            </Dialog.Title>
            <Dialog.CloseButton />
          </Dialog.Header>
          <Dialog.Body className="flex flex-col gap-4">
            <Dialog.Description>
              <Typography as="span" variant="body-sm" tone="muted">
                I2Q saves your pick for this case. Nothing goes to the customer.
              </Typography>
            </Dialog.Description>
            <div className="border-border-subtle rounded-md border px-3 py-2">
              <Typography variant="caption" tone="subtle" className="block">
                Customer asked
              </Typography>
              <Typography as="span" variant="body-sm" className="text-fg font-semibold break-words">
                {line.enquiryText}
              </Typography>
            </div>

            {candidates.length > 0 && (
              <div>
                <Typography variant="caption" tone="subtle">
                  Proposed by I2Q
                </Typography>
                <ul className="flex flex-col">
                  {candidates.map((option) => (
                    <PickRow
                      key={option.item}
                      option={option}
                      lineText={line.enquiryText}
                      suggested
                      busy={decide.isPending}
                      onPick={pick}
                    />
                  ))}
                </ul>
              </div>
            )}

            <div className="flex flex-col gap-2">
              <Input
                type="search"
                value={text}
                maxLength={60}
                onChange={(event) => setText(event.target.value)}
                placeholder="Search the catalogue by item number or name"
                aria-label="Search the item catalogue"
                startSlot={<Icon icon={Search} size="sm" className="text-fg-subtle" />}
              />
              {typed.length > 0 && typed.length < SEARCH_MIN_CHARS && (
                <Typography variant="caption" tone="subtle">
                  Type at least {SEARCH_MIN_CHARS} characters.
                </Typography>
              )}
              {searching && <Spinner size="sm" label="Searching" />}
              {!searching && search.isError && typed.length >= SEARCH_MIN_CHARS && (
                <Typography variant="caption" className="text-danger-fg" role="alert">
                  {searchStatus === 422
                    ? (workFailureText(search.error, "line") ?? "")
                    : "The item catalogue is not reachable right now."}
                </Typography>
              )}
              {!searching &&
                !search.isError &&
                typed.length >= SEARCH_MIN_CHARS &&
                (results.length === 0 ? (
                  <Typography variant="caption" tone="subtle">
                    No item in this catalogue matches the search.
                  </Typography>
                ) : (
                  <ul className="flex max-h-72 flex-col overflow-y-auto">
                    {results.map((option) => (
                      <PickRow
                        key={`${option.area}-${option.item}`}
                        option={option}
                        lineText={line.enquiryText}
                        suggested={candidates.some((known) => known.item === option.item)}
                        busy={decide.isPending}
                        onPick={pick}
                      />
                    ))}
                  </ul>
                ))}
            </div>

            {error && (
              <Alert tone="warning" title="Not saved">
                {error}
              </Alert>
            )}
          </Dialog.Body>
          <Dialog.Footer className="flex justify-end gap-2">
            <Button variant="outline" tone="neutral" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
          </Dialog.Footer>
        </Dialog.Popup>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
